import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowUpIcon, MicIcon, PlusIcon, SparklesIcon } from 'lucide-react';
import { heroPrompts } from '../data/homepage';
import { AtnaConversationDialog } from './demandes/AtnaConversationDialog';
import { InscriptionDialog } from './loisirs/InscriptionDialog';
import { PermisDialog } from './permis/PermisDialog';
import { detectCategory } from '../hooks/useInscriptionConversation';
import { detectPermitType } from '../hooks/usePermisConversation';
const PANEL_LAYOUT_ID = 'atna-hero-panel';
type Flow = 'signalement' | 'inscription' | 'permis';

/** Choisit le flux ATNA le plus probable à partir de la phrase du citoyen. */
function detectFlow(text: string): Flow {
  const lower = text.toLowerCase();
  // Un projet de travaux ou un type de permis reconnu ouvre la demande de permis.
  const wantsPermit = /construir|permis|travaux|rénov|renov|agrandir|installer|abattre|piscine|clôture|cloture/.test(lower) || detectPermitType(lower) !== null;
  if (wantsPermit) return 'permis';
  const wantsRegistration = /inscri|cours|activit|camp|session|natation/.test(lower) || detectCategory(lower) !== null;
  return wantsRegistration ? 'inscription' : 'signalement';
}
export function HeroAtna() {
  const navigate = useNavigate();
  const [value, setValue] = useState('');
  const [conversation, setConversation] = useState<{
    flow: Flow;
    text: string;
  } | null>(null);
  const openConversation = (text: string, flow?: Flow) => {
    const trimmed = text.trim();
    if (!trimmed) return;
    setConversation({
      flow: flow ?? detectFlow(trimmed),
      text: trimmed
    });
    setValue('');
  };
  return <section aria-labelledby="hero-atna-titre" className="w-full rounded-3xl border border-white/60 bg-white p-5 shadow-2xl shadow-ville-navy/20 sm:p-6" data-id="element-1635" data-mp="18bvrgx6oqqvc">
      <h2 id="hero-atna-titre" className="sr-only" data-id="element-1636" data-mp="3t2wahb4qxmfo">
        Assistant ATNA
      </h2>

      {conversation ?
    // Garde la hauteur pendant que la barre se transforme en dialogue.
    <div className="h-[54px]" aria-hidden="true" data-id="element-1637" data-mp="1tnumuuupzuhi" /> : <motion.div layoutId={PANEL_LAYOUT_ID} style={{
      borderRadius: 999
    }} transition={{
      duration: 0.28,
      ease: [0.23, 1, 0.32, 1]
    }} data-id="element-1638" data-mp="1tomkeb7vo2af">
          <form onSubmit={event => {
        event.preventDefault();
        openConversation(value);
      }} className="flex items-center gap-2 rounded-full border border-slate-200 bg-white py-1.5 pl-3 pr-1.5 transition-colors duration-150 ease-ville focus-within:border-ville-indigo" data-id="element-1639" data-mp="1kuhkl2arngir">
            <button type="button" onClick={() => openConversation(value)} aria-label="Joindre une photo ou un document" className="grid h-9 w-9 shrink-0 place-items-center rounded-full text-slate-500 transition-colors duration-150 ease-ville hover:bg-ville-mist hover:text-ville-deep focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-1640" data-mp="1603c3votvpic">
              <PlusIcon className="h-5 w-5" aria-hidden="true" data-id="element-1641" data-mp="n1hzy3dg44rm" />
            </button>
            <label htmlFor="hero-atna-input" className="sr-only" data-id="element-1642" data-mp="rsq86w31hk37">
              Demander à ATNA
            </label>
            <input id="hero-atna-input" type="text" value={value} onChange={event => setValue(event.target.value)} placeholder="Demander à ATNA…" className="min-w-0 flex-1 border-0 bg-transparent py-2 text-base text-ville-navy placeholder:text-slate-400 focus:outline-none" data-id="element-1643" data-mp="vuuzvp9xg6n7" />
            <button type="button" aria-label="Dicter votre question" className="grid h-9 w-9 shrink-0 place-items-center rounded-full text-slate-500 transition-colors duration-150 ease-ville hover:bg-ville-mist hover:text-ville-deep focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-1644" data-mp="873meicnhnkc">
              <MicIcon className="h-5 w-5" aria-hidden="true" data-id="element-1645" data-mp="1n2ddiqx941s4" />
            </button>
            <button type="submit" aria-label="Envoyer la question à ATNA" className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-ville-blue text-white transition-transform duration-150 ease-ville hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo focus-visible:ring-offset-2" data-id="element-1646" data-mp="1i3sa6impd1zg">
              <ArrowUpIcon className="h-5 w-5" aria-hidden="true" data-id="element-1647" data-mp="210l6yov2wkyj" />
            </button>
          </form>
        </motion.div>}

      <ul className="mt-3 flex flex-wrap gap-2" data-id="element-1648" data-mp="2fppo02q50j52">
        {heroPrompts.map(prompt => <li key={prompt.label} data-id="element-1649" data-mp="2arxy1e91z73i">
            <button type="button" onClick={() => prompt.label === 'Inscrire mon enfant' ? openConversation(prompt.label, 'inscription') : navigate(prompt.to)} className="rounded-full border border-slate-200 bg-white px-3.5 py-2 text-sm font-semibold text-ville-navy transition-colors duration-150 ease-ville hover:border-ville-indigo hover:bg-ville-indigo-soft hover:text-ville-indigo focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-1650" data-mp="2or73bivq0gsp">
              {prompt.label}
            </button>
          </li>)}
      </ul>

      <p className="mt-3 flex items-center gap-1.5 text-xs font-semibold text-ville-indigo" data-id="element-1651" data-mp="2kltowh4ptrsj">
        <SparklesIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-1652" data-mp="2pfpkjcxib2kj" />
        ATNA connaît les services de Baie-Trinité
      </p>

      <AnimatePresence data-id="element-1653" data-mp="2andykgdiimk3">
        {conversation?.flow === 'signalement' && <AtnaConversationDialog key="atna-hero-dialog" initialText={conversation.text} layoutId={PANEL_LAYOUT_ID} onClose={() => setConversation(null)} data-id="element-1654" data-mp="2fol7wjvaztsv" />}
        {conversation?.flow === 'inscription' && <InscriptionDialog key="atna-hero-inscription" initialText={conversation.text} layoutId={PANEL_LAYOUT_ID} onClose={() => setConversation(null)} data-id="element-1655" data-mp="c8ggwgkf1yad" />}
        {conversation?.flow === 'permis' && <PermisDialog key="atna-hero-permis" initialText={conversation.text} layoutId={PANEL_LAYOUT_ID} onClose={() => setConversation(null)} data-id="element-1656" data-mp="3ulxwfxjdz3fe" />}
      </AnimatePresence>
    </section>;
}